/* eslint-disable react/jsx-no-comment-textnodes */
import React, { useState } from 'react';

// ── layout helpers ────────────────────────────────────────────────────────────

function Section({ id, title, accent, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const colors = { b: 'var(--blue)', g: 'var(--green)', a: 'var(--amber)', r: 'var(--red)', v: 'var(--violet)' };
  const c = colors[accent] || colors.b;
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderTop: `2px solid ${c}`, borderRadius: 12, padding: '18px 22px', marginBottom: 14 }} id={id}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, marginBottom: open ? 14 : 0 }}>
        <span style={{ fontFamily: 'var(--f)', fontSize: 17, fontWeight: 800, letterSpacing: '-.04em', color: 'var(--t0)' }}>{title}</span>
        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <span style={{ color: 'var(--t3)', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && children}
    </div>
  );
}

function Formula({ children }) {
  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--border2)', borderLeft: '3px solid var(--blue)', borderRadius: 8, padding: '14px 18px', fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 2.1, margin: '12px 0', overflowX: 'auto' }}>
      {children}
    </div>
  );
}

function Fline({ children }) { return <div style={{ display: 'block', whiteSpace: 'nowrap' }}>{children}</div>; }
function Fc({ children }) { return <span style={{ color: 'var(--t2)', fontSize: 10 }}>{children}</span>; }
function Fk({ children }) { return <span style={{ color: 'var(--blue)', fontWeight: 600 }}>{children}</span>; }
function Fv({ children }) { return <span style={{ color: 'var(--green)', fontWeight: 600 }}>{children}</span>; }
function Fo({ children }) { return <span style={{ color: 'var(--amber)' }}>{children}</span>; }

function P({ children }) {
  return <p style={{ fontSize: 12, color: 'var(--t1)', lineHeight: 1.85, marginBottom: 10 }}>{children}</p>;
}

function H3({ children }) {
  return <h3 style={{ fontFamily: 'var(--f)', fontSize: 13, fontWeight: 700, letterSpacing: '-.02em', margin: '16px 0 7px', color: 'var(--t0)' }}>{children}</h3>;
}

function ImpBox({ children }) {
  return (
    <div style={{ background: 'linear-gradient(135deg,rgba(245,158,11,.08),rgba(245,158,11,.02))', border: '1px solid rgba(245,158,11,.3)', borderLeft: '3px solid var(--amber)', borderRadius: 10, padding: '14px 18px', margin: '12px 0' }}>
      {children}
    </div>
  );
}

function Step({ n, title, body, formula }) {
  return (
    <div style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontFamily: 'var(--f)', fontSize: 24, fontWeight: 800, color: 'var(--border2)', flexShrink: 0, width: 30, lineHeight: 1, marginTop: 2, letterSpacing: '-.04em' }}>{n}</div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t0)', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 11, color: 'var(--t1)', lineHeight: 1.7 }}>{body}</div>
        {formula && (
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, background: 'var(--bg3)', padding: '5px 10px', borderRadius: 6, marginTop: 6, display: 'inline-block', color: 'var(--blue)', border: '1px solid var(--border)', borderLeft: '2px solid var(--blue)' }}>
            {formula}
          </div>
        )}
      </div>
    </div>
  );
}

function CompTable({ rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, margin: '10px 0' }}>
      <thead>
        <tr>
          {['Component 组成项', 'Weight 权重', 'What it measures 衡量内容', 'Scoring logic 评分逻辑', 'Data source 数据来源'].map(h => (
            <th key={h} style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '7px 12px', borderBottom: '1px solid var(--border)', textAlign: 'left', background: 'var(--bg3)' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', fontWeight: 600, color: 'var(--t0)', minWidth: 180 }}>{r[0]}</td>
            <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', color: 'var(--t1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 72, height: 4, background: 'var(--bg4)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--blue)', width: r[1] }} />
                </div>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--blue)', fontWeight: 600 }}>{r[1]}</span>
              </div>
            </td>
            <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', color: 'var(--t1)', fontSize: 11, lineHeight: 1.5, maxWidth: 200 }}>{r[2]}</td>
            <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', color: 'var(--t1)', fontSize: 11, lineHeight: 1.5, fontFamily: 'var(--mono)', maxWidth: 220 }}>{r[3]}</td>
            <td style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', color: 'var(--t2)', fontSize: 10, whiteSpace: 'nowrap' }}>{r[4]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ZoneTable({ rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, margin: '10px 0' }}>
      <thead>
        <tr>
          {['Score 分数', 'Label 标签', 'Meaning 含义', 'Signal 信号'].map(h => (
            <th key={h} style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '7px 12px', borderBottom: '1px solid var(--border)', textAlign: 'left', background: 'var(--bg3)' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', fontWeight: 700, color: r[2] }}>{r[0]}</td>
            <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}><span className={`badge badge-${r[3]}`}>{r[1]}</span></td>
            <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--t1)', fontSize: 11 }}>{r[4]}</td>
            <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--t1)', fontSize: 11 }}>{r[5]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── bilingual helpers ─────────────────────────────────────────────────────────

function BT({ en, zh }) {
  return (
    <>
      {en}
      <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 600, color: 'var(--t2)', letterSpacing: '-.01em' }}>{zh}</span>
    </>
  );
}

function Bi({ en, zh }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--t1)', lineHeight: 1.85 }}>{en}</div>
      <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.75, marginTop: 3, paddingLeft: 10, borderLeft: '2px solid var(--border)' }}>{zh}</div>
    </div>
  );
}

function BiH3({ en, zh }) {
  return (
    <h3 style={{ fontFamily: 'var(--f)', fontSize: 13, fontWeight: 700, letterSpacing: '-.02em', margin: '16px 0 7px', color: 'var(--t0)' }}>
      {en}
      <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--t2)', marginLeft: 8 }}>{zh}</span>
    </h3>
  );
}

function BiBullets({ items }) {
  return (
    <ul style={{ margin: '8px 0', padding: '0 0 0 18px' }}>
      {items.map((item, i) => (
        <li key={i} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: 'var(--t1)', lineHeight: 1.6 }}>{item.en}</div>
          <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.5 }}>{item.zh}</div>
        </li>
      ))}
    </ul>
  );
}

function KeyNote({ children }) {
  return (
    <div style={{ background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.25)', borderLeft: '3px solid var(--amber)', borderRadius: 8, padding: '10px 14px', margin: '10px 0' }}>
      <span style={{ color: 'var(--amber)', fontWeight: 800, marginRight: 6 }}>★</span>
      <span style={{ fontSize: 12, color: 'var(--t0)' }}>{children}</span>
    </div>
  );
}

// ── TOC ───────────────────────────────────────────────────────────────────────

const TOC_ITEMS = [
  { id: 's-overview',   label: '① Overview 概述' },
  { id: 's-inputs',     label: '② Inputs 输入' },
  { id: 's-indices',    label: '③ Indices 四大指数' },
  { id: 's-bayes',      label: '④ Bayesian 贝叶斯' },
  { id: 's-safety',     label: '⑤ Safety 安全库存' },
  { id: 's-rec',        label: '⑥ Decision 决策' },
  { id: 's-volume',     label: '⑦ Volume 采购量' },
  { id: 's-margin',     label: '⑧ Margins 利润' },
  { id: 's-incomplete', label: '⑨ Incomplete 不完整' },
  { id: 's-veritas',    label: '⑩ VERITAS' },
  { id: 's-next',       label: '⑪ Next 规划' },
  { id: 's-glossary',   label: '⑫ Glossary 词汇' },
];

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ModelLogic() {
  return (
    <div>
      <div className="page-hdr">
        <h2>Model Logic <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--t2)', marginLeft: 8 }}>模型逻辑</span></h2>
        <p>Complete documentation of every formula, weight, and decision rule in PP MAP — current through Package 1. 完整记录 PP MAP 所有公式、权重与决策规则，内容更新至 Package 1。</p>
      </div>

      {/* TOC */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
        {TOC_ITEMS.map(t => (
          <a key={t.id} href={`#${t.id}`} style={{ fontSize: 11, padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--blue)', textDecoration: 'none', fontFamily: 'var(--mono)', fontWeight: 500 }}>
            {t.label}
          </a>
        ))}
      </div>

      {/* ① OVERVIEW */}
      <Section id="s-overview" title={<BT en="① Overview — What PP MAP Does" zh="① 概述 — PP MAP 的作用" />} accent="b">
        <Bi
          en="PP MAP is a procurement decision-support system for BOPP film plants. It processes market signals and internal inventory data to produce a clear, auditable procurement recommendation with full math transparency."
          zh="PP MAP 是为 BOPP 薄膜工厂设计的采购决策支持系统，整合市场信号与内部库存数据，生成透明可审查的采购建议。"
        />
        <Bi
          en="Flow: Inputs → Four Indices (STI/MSI/DPI/OTI) → Bayesian Scenario Engine → 7-day P10/P50/P90 Forecast → Recommendation → Volume Sizing → Action."
          zh="运行流程：输入 → 四大指数（STI/MSI/DPI/OTI）→ 贝叶斯情景引擎 → 7 天 P10/P50/P90 预测 → 采购建议 → 采购量测算 → 执行。"
        />
        <Bi
          en="Every recommendation shows its math. Nothing is a black box — procurement managers can audit and override any signal."
          zh="每条建议均展示其计算依据，系统无任何黑盒逻辑，采购经理可随时审查并手动覆盖任意信号。"
        />
        <Formula>
          <Fline><Fk>STI</Fk>  = Supply Tightness Index      <Fc>// PP 供应紧张指数 (0–100)</Fc></Fline>
          <Fline><Fk>MSI</Fk>  = Market Supply Index         <Fc>// 市场供应指数 (0–100)</Fc></Fline>
          <Fline><Fk>DPI</Fk>  = BOPP Demand Pressure Index  <Fc>// BOPP 需求压力指数 (0–100)</Fc></Fline>
          <Fline><Fk>OTI</Fk>  = Order Trend Index           <Fc>// 订单趋势指数 (−100 to +100)</Fc></Fline>
          <Fline>&nbsp;</Fline>
          <Fline>Inputs <Fo>→</Fo> [STI, MSI, DPI, OTI] <Fo>+</Fo> Bayesian <Fo>→</Fo> <Fv>E[PP_7d]</Fv> <Fo>→</Fo> Recommendation <Fo>→</Fo> Volume</Fline>
        </Formula>
      </Section>

      {/* ② INPUTS */}
      <Section id="s-inputs" title={<BT en="② Inputs" zh="② 输入数据" />} accent="b">

        <BiH3 en="2.1 Live Auto-Fetched (every 15 min via /api/live-prices proxy)" zh="2.1 实时自动拉取（每 15 分钟，经 /api/live-prices 代理）" />
        <BiBullets items={[
          { en: 'WTI crude oil price (USD/bbl)', zh: 'WTI 原油价格（美元/桶）' },
          { en: 'Brent crude oil price (USD/bbl)', zh: 'Brent 原油价格（美元/桶）' },
          { en: 'DCE PP M1 front-month futures (¥/MT) — Dalian Commodity Exchange', zh: '大连商品交易所 PP M1 主力合约（元/吨）' },
          { en: 'Polymarket Hormuz conflict probability (%)', zh: 'Polymarket 霍尔木兹冲突概率（%）' },
          { en: 'USD/CNY exchange rate via open.er-api.com', zh: '美元/人民币汇率，来源：open.er-api.com' },
        ]} />
        <KeyNote>Freshness badge downgrades to "stale" after 20 minutes without a successful fetch. 数据徽章在 20 分钟内未成功刷新后降级为"过时"状态。</KeyNote>

        <BiH3 en="2.2 Manual Inputs (require human entry in Data Entry)" zh="2.2 手动输入（需在数据录入页面填写）" />
        <BiBullets items={[
          { en: 'Spot PP Qingdao (¥/MT) — China domestic reference price', zh: '青岛现货 PP 价格（元/吨）— 中国国内基准价格' },
          { en: 'Hormuz status, traffic level (%), conflict weeks in progress, recent events', zh: '霍尔木兹状态、通行量（%）、冲突持续周数、近期事件' },
          { en: 'SEA BOPP CFR price range: low / high (USD/MT)', zh: '东南亚 BOPP CFR 价格区间：低位 / 高位（美元/吨）' },
          { en: 'Conversion cost — interim default ¥1,800/MT', zh: '加工转换成本 — 过渡默认值 ¥1,800/吨' },
          { en: 'Scenario band offsets — 12 configurable fields: low/mid/high × 4 scenarios', zh: '情景价格区间偏移 — 12 个可配置字段：低/中/高 × 4 个情景' },
        ]} />

        <BiH3 en="2.3 Internal Systems — KAPS / KICP" zh="2.3 内部系统 — KAPS / KICP" />
        <Bi
          en="KAPS (MRP system): realtime stock (MT), in-transit volume (MT), PR confirmed (MT), weighted average cost WAC (¥/MT), safety stock level (MT), daily demand (MT/day)."
          zh="KAPS（MRP 系统）：实时库存（吨）、在途量（吨）、已确认采购申请（吨）、加权平均成本 WAC（元/吨）、安全库存水位（吨）、日需求量（吨/天）。"
        />
        <Bi
          en="KICP (film order system): Gloss 18µm sell price, portfolio average sell price, portfolio quantity (current month + prior month), prior month average sell price."
          zh="KICP（薄膜订单系统）：光膜 18µm 销售单价、组合平均销售单价、组合销售量（本月 + 上月）、上月平均销售单价。"
        />
        <Bi
          en="Current status: manual entry in Data Entry screen. API integration is in IT queue."
          zh="当前状态：通过数据录入页面手动输入。API 自动集成已列入 IT 开发队列。"
        />
        <KeyNote>
          KEY RULE: if realtime stock OR daily demand is null → model does not run (Incomplete State shown). Zero is valid (plant offline). Null means not entered.{' '}
          关键规则：若实时库存或日需求量为空（null）→ 模型不运行，显示不完整状态。零值有效（产线停产）；空值意味着尚未录入。
        </KeyNote>
      </Section>

      {/* ③ FOUR INDICES */}
      <Section id="s-indices" title={<BT en="③ The Four Indices" zh="③ 四大指数" />} accent="b">

        <BiH3 en="3.1 STI — Supply Tightness Index (0–100)" zh="3.1 STI — 供应紧张指数（0–100）" />
        <Bi
          en="Measures how tight the physical PP supply situation is (0 = abundant, 100 = extreme shortage). Weighted composite of five supply indicators following Argus/ICIS methodology."
          zh="衡量 PP 实物供应紧张程度（0 = 充裕，100 = 极度短缺）。依据 Argus/ICIS 方法论，由五个供应指标加权合成。"
        />
        <Formula>
          <Fline><Fk>STI</Fk> <Fo>=</Fo> (A2 <Fo>×</Fo> <Fv>0.22</Fv>) <Fo>+</Fo> (B2 <Fo>×</Fo> <Fv>0.28</Fv>) <Fo>+</Fo> (C2 <Fo>×</Fo> <Fv>0.18</Fv>) <Fo>+</Fo> (D2 <Fo>×</Fo> <Fv>0.20</Fv>) <Fo>+</Fo> (E2 <Fo>×</Fo> <Fv>0.12</Fv>)</Fline>
          <Fline><Fc>// A2=plant ops · B2=Hormuz · C2=inventory · D2=feedstock · E2=export cushion</Fc></Fline>
        </Formula>
        <CompTable rows={[
          ['A2 — Plant ops 产能开工率', '22%', 'PP plant capacity offline or in maintenance\nPP产能停工或在检修', 'op≥85%→20 · ≥75%→50 · ≥65%→70 · <65%→85', 'SunSirs / CPCIF weekly'],
          ['B2 — Hormuz + VLCC 霍尔木兹+VLCC', '28%', 'Import flow disruption — how much PP is blocked from entering China\n进口流量中断', 'B2=(1−hormuz/100)×100×(1+VLCC_premium/200)', 'MarineTraffic + Baltic Exchange'],
          ['C2 — Inventory weeks 库存周数', '18%', 'Weeks of PP supply vs consumption — from KAPS stock + daily demand\n库存保障周数', '≥4wks→10 · ≥3→35 · ≥2→65 · ≥1→85 · <1→95', 'KAPS MRP (realtime)'],
          ['D2 — Propylene–PP spread 丙烯-PP价差', '20%', 'Margin signal for PP producers — how profitable to make PP?\nPP生产商利润信号', 'spread=PP−prop×1.02. ≥¥900→10 · ≥¥600→40 · ≥¥400→65 · ≥¥200→80 · <¥200→95', 'SCI99 daily'],
          ['E2 — Naphtha crack 石脑油裂解价差', '12%', 'Feedstock economics for naphtha-based PP production\n石脑油基PP原料经济性', '>$150→90 · >$120→80 · >$80→45 · >$60→25 · <$60→15', 'Argus / Platts CFR Japan'],
        ]} />
        <H3>STI Interpretation Bands 分段解读</H3>
        <ZoneTable rows={[
          ['≥70', 'Critical 危急',     'var(--red)',   'red',     'Supply severely constrained — imports blocked, plants offline 供应严重受限', 'Buy immediately 立即采购'],
          ['50–69', 'Elevated 偏紧',   'var(--amber)', 'amb',     'Supply noticeably constrained — one or more factors active 供应明显收紧', 'Buy on dips 逢低买入'],
          ['30–49', 'Moderate 适中',   'var(--t1)',    'neutral', 'Normal supply with some tension 正常供应，存在一定紧张', 'Normal rhythm 正常节奏'],
          ['<30',  'Comfortable 充裕', 'var(--green)', 'grn',     'Supply ample — can wait for better prices 供应充裕', 'Hold or buy on dips 持仓或逢低采购'],
        ]} />
        <ImpBox>
          <strong style={{ color: 'var(--amber)' }}>Why B2 (Hormuz) carries 28%:</strong>
          <span style={{ fontSize: 12, color: 'var(--t1)', marginLeft: 6 }}>In normal conditions D2 (propylene–PP spread) is the primary driver. During active Hormuz disruptions import flow dominates. Calibrated via OLS regression on China PP 2020–2025 — Hormuz traffic showed the highest coefficient (−1.24 per 10% traffic drop, p=0.004). 正常情况下 D2（丙烯-PP 价差）为主驱动因素；霍尔木兹中断期间，进口流量占主导。</span>
        </ImpBox>

        <BiH3 en="3.2 MSI — Market Supply Index (0–100)" zh="3.2 MSI — 市场供应指数（0–100）" />
        <Bi
          en="Captures industry-level supply pressure distinct from Kunlene's own inventory position. Inputs: BOPP plant operating rate, supplier allocation flags, SEA supply disruption signals."
          zh="反映行业层面的供应压力，与昆仑恩自身库存状况相互独立。输入项：BOPP 工厂开工率、供应商配额标志、东南亚供应中断信号。"
        />
        <Bi
          en="Purpose: separates market-wide BOPP supply tightness from the STI's physical PP focus. A market can be short BOPP without PP being globally scarce."
          zh="用途：将市场整体 BOPP 供应紧张与 STI 的实物 PP 聚焦相区分。市场 BOPP 可短缺，而全球 PP 供应未必稀缺。"
        />

        <BiH3 en="3.3 DPI — BOPP Demand Pressure Index (0–100)" zh="3.3 DPI — BOPP 需求压力指数（0–100）" />
        <Bi
          en="Measures how strong actual downstream BOPP demand is at the product layer. Combines industry operating rate, Kunlene's KICP order momentum, and anchor product margin pressure."
          zh="在产品层面衡量 BOPP 薄膜下游实际需求强度。综合行业开工率、昆仑恩 KICP 订单动量及锚定产品利润压力。"
        />
        <Formula>
          <Fline><Fk>DPI</Fk> <Fo>=</Fo> (util_score <Fo>×</Fo> <Fv>0.40</Fv>) <Fo>+</Fo> (order_score <Fo>×</Fo> <Fv>0.35</Fv>) <Fo>+</Fo> (margin_score <Fo>×</Fo> <Fv>0.25</Fv>)</Fline>
          <Fline><Fc>// util_score:   BOPP 行业开工率 / industry operating rate</Fc></Fline>
          <Fline><Fc>// order_score:  KICP 环比销量动量 / MoM volume momentum from KICP</Fc></Fline>
          <Fline><Fc>// margin_score: 光膜 18µm 锚定利润压力 / Gloss 18µm anchor margin pressure</Fc></Fline>
        </Formula>
        <KeyNote>
          DPI degrades visibly (amber ★ on gauge) when KICP inputs are null — model continues on available data but marks the gap.{' '}
          当 KICP 输入为空时，DPI 仪表盘显示琥珀色 ★ 警告——模型继续运行但标注数据缺口。
        </KeyNote>

        <BiH3 en="3.4 OTI — Order Trend Index (−100 to +100)" zh="3.4 OTI — 订单趋势指数（−100 至 +100）" />
        <Bi
          en="Measures whether Kunlene's film order book is growing or contracting. Uses KICP data exclusively. Negative = contracting; positive = growing. ±3% threshold for flat signal."
          zh="衡量昆仑恩薄膜订单量是增长还是收缩，完全依赖 KICP 数据。负值 = 收缩；正值 = 增长。±3% 以内视为持平信号。"
        />
        <Formula>
          <Fline><Fk>OTI</Fk> <Fo>=</Fo> (vol_momentum <Fo>×</Fo> <Fv>0.50</Fv>) <Fo>+</Fo> (price_momentum <Fo>×</Fo> <Fv>0.30</Fv>) <Fo>+</Fo> (revision_score <Fo>×</Fo> <Fv>0.20</Fv>)</Fline>
          <Fline>vol_momentum   <Fo>=</Fo> (current_vol <Fo>−</Fo> prior_vol) <Fo>/</Fo> prior_vol <Fo>×</Fo> <Fv>100</Fv>, clipped ±100</Fline>
          <Fline>price_momentum <Fo>=</Fo> (current_avg_sell <Fo>−</Fo> prior_avg_sell) <Fo>/</Fo> prior_avg_sell <Fo>×</Fo> <Fv>100</Fv>, clipped ±100</Fline>
          <Fline>revision_score <Fo>=</Fo> <Fo>−</Fo>net_revision_pct <Fo>×</Fo> <Fv>10</Fv>, clipped ±100  <Fc>// cancellations penalise OTI / 取消单降低OTI</Fc></Fline>
        </Formula>
        <KeyNote>
          OTI degrades visibly (amber ★ on gauge) when KICP inputs are null.{' '}
          当 KICP 输入为空时，OTI 仪表盘显示琥珀色 ★ 警告。
        </KeyNote>
      </Section>

      {/* ④ BAYESIAN */}
      <Section id="s-bayes" title={<BT en="④ Bayesian Scenario Engine" zh="④ 贝叶斯情景引擎" />} accent="v">
        <Bi
          en="Four geopolitical scenarios — peace, stalemate, escalation, strike — each assigned a probability. Computed from Hormuz traffic level and conflict duration weeks, then updated via Bayesian inference when events are observed."
          zh="四种地缘政治情景——和平、僵局、升级、军事打击——各自赋予概率。依据霍尔木兹通行量与冲突持续周数计算，并在新事件发生时通过贝叶斯推断更新。"
        />
        <BiBullets items={[
          { en: 'Price bands are spot-anchored: scenario ranges move with the current PP spot price', zh: '情景价格区间以现货价格为锚，随当前 PP 现货价格浮动' },
          { en: '12 configurable offsets in Data Entry: low/mid/high × 4 scenarios', zh: '数据录入页面可配置 12 个偏移参数：低/中/高 × 4 个情景' },
          { en: '7-day P10/P50/P90 forecast derived from probability-weighted scenario ranges', zh: '7 天 P10/P50/P90 预测由情景概率加权区间推导得出' },
          { en: 'Optional Polymarket blend (70:30): 70% Bayesian + 30% Polymarket when signal available', zh: '可选 Polymarket 混合（70:30）：有信号时取 70% 贝叶斯 + 30% Polymarket' },
          { en: 'Single 7-day horizon today; multi-horizon (7/30/60d) is future work', zh: '当前仅支持 7 天预测窗口；多时段预测（7/30/60 天）为未来规划' },
        ]} />
        <Formula>
          <Fline><Fc>// Expected PP 7-day price  /  PP 价格 7 天期望值</Fc></Fline>
          <Fline><Fk>E[PP_7d]</Fk> <Fo>=</Fo> Σ P(scenario_i) <Fo>×</Fo> mid_price_i</Fline>
          <Fline>&nbsp;</Fline>
          <Fline><Fc>// Spot-anchored bands (mid offsets, illustrative):</Fc></Fline>
          <Fline>Peace:      spot <Fo>+</Fo> low_offset    <Fc>// typically spot − ¥800 to − ¥300</Fc></Fline>
          <Fline>Stalemate:  spot <Fo>+</Fo> mid_offset    <Fc>// near spot ± ¥200</Fc></Fline>
          <Fline>Escalation: spot <Fo>+</Fo> high_offset   <Fc>// spot + ¥800 to + ¥1,500</Fc></Fline>
          <Fline>Strike:     spot <Fo>+</Fo> max_offset    <Fc>// spot + ¥2,000 to + ¥4,000</Fc></Fline>
          <Fline>&nbsp;</Fline>
          <Fline><Fc>// P10 = probability-weighted optimistic bound  /  乐观边界</Fc></Fline>
          <Fline><Fc>// P90 = probability-weighted pessimistic bound /  悲观边界</Fc></Fline>
          <Fline><Fc>// if P10 > today_spot × 1.05  →  BUY_NOW (downside risk too high)</Fc></Fline>
        </Formula>
      </Section>

      {/* ⑤ SAFETY */}
      <Section id="s-safety" title={<BT en="⑤ Safety &amp; Coverage" zh="⑤ 安全库存与覆盖天数" />} accent="a">
        <Bi
          en="Safety and coverage calculations are performed against KAPS data. These drive the highest-priority decision rule: a safety breach overrides all price signals."
          zh="安全库存与覆盖天数计算依据 KAPS 数据执行，驱动最高优先级决策规则：安全库存告警将覆盖所有价格信号。"
        />
        <Formula>
          <Fline>coverage_days  <Fo>=</Fo> (stock <Fo>+</Fo> transit <Fo>+</Fo> pr_confirmed) <Fo>/</Fo> daily_demand  <Fc>// 总覆盖天数</Fc></Fline>
          <Fline>days_to_breach <Fo>=</Fo> (stock <Fo>−</Fo> safety_stock) <Fo>/</Fo> daily_demand               <Fc>// 安全库存告警天数</Fc></Fline>
          <Fline>&nbsp;</Fline>
          <Fline><Fc>// Urgency thresholds  /  紧急程度阈值</Fc></Fline>
          <Fline>days_to_breach <Fo>&lt;</Fo> <Fv>30</Fv>  <Fo>→</Fo> Critical  <Fc>// 危急 — overrides all, buy now</Fc></Fline>
          <Fline>days_to_breach <Fo>&lt;</Fo> <Fv>45</Fv>  <Fo>→</Fo> Urgent    <Fc>// 紧急</Fc></Fline>
          <Fline>days_to_breach <Fo>≥</Fo> <Fv>45</Fv>  <Fo>→</Fo> Normal    <Fc>// 正常</Fc></Fline>
        </Formula>
        <KeyNote>
          Hard stop: if KAPS stock OR daily demand is null → no safety calculation, no recommendation, IncompleteState shown with "Go to Data Entry" CTA.{' '}
          强制中止：若 KAPS 库存或日需求量为空 → 不执行安全计算，不生成建议，显示 IncompleteState 并提示"前往数据录入"。
        </KeyNote>
        <BiBullets items={[
          { en: 'Coverage days: total forward cover across stock + in-transit + confirmed PRs', zh: '覆盖天数：库存 + 在途量 + 已确认采购申请的合计保障天数' },
          { en: 'Days to safety breach: how long before stock falls below safety stock — not the same as total coverage', zh: '安全库存告警天数：库存降至安全库存水位以下所需时间——不同于总覆盖天数' },
          { en: 'Zero daily demand is valid (plant offline) — treated as effectively infinite coverage', zh: '日需求量为零有效（产线停产）——视为无限覆盖天数处理' },
        ]} />
      </Section>

      {/* ⑥ RECOMMENDATION ENGINE */}
      <Section id="s-rec" title={<BT en="⑥ Recommendation Engine" zh="⑥ 采购建议引擎" />} accent="r">

        <BiH3 en="6.1 Six Possible Actions" zh="6.1 六种可能的操作建议" />
        <BiBullets items={[
          { en: 'BUY_NOW · Safety Critical — safety breach imminent; buy regardless of price', zh: 'BUY_NOW · 安全危急 — 安全库存即将告急，不计价格立即购买' },
          { en: 'BUY_NOW · Price Risk — price signal compelling; accumulate now before prices rise', zh: 'BUY_NOW · 价格风险 — 价格信号显著，在涨价前立即大量采购' },
          { en: 'BUY_TRANCHE — staged purchase; buy a meaningful portion now, not everything', zh: 'BUY_TRANCHE — 分批采购；现在采购一定比例量，非全部' },
          { en: 'HOLD — two variants: (a) safety coverage only when coverage <60d; (b) baseline no-strong-signal', zh: 'HOLD — 两种形式：(a) 覆盖天数 <60 天时仅维持安全库存；(b) 无强信号基准' },
          { en: 'WAIT — escalated from HOLD when coverage is excessive + price trend is down', zh: 'WAIT — 由 HOLD 升级，适用于覆盖过度 + 价格下行趋势场景' },
          { en: 'INCOMPLETE — critical KAPS inputs missing; model does not run', zh: 'INCOMPLETE — 关键 KAPS 输入缺失；模型不运行' },
        ]} />

        <BiH3 en="6.2 Decision Logic — Priority Order" zh="6.2 决策逻辑 — 优先顺序" />
        <Bi
          en="Safety check fires first and is binding — it overrides all price signals. After safety, STI/MSI/DPI/OTI compose the signal. First rule that matches wins."
          zh="安全检查最先触发且为强制优先项，覆盖所有价格信号。安全检查通过后，由 STI/MSI/DPI/OTI 组合信号，首条触发规则生效。"
        />
        <div style={{ marginBottom: 12 }}>
          <Step n="1"
            title="Safety breach imminent → BUY_NOW · Safety Critical (overrides all) / 安全库存即将告急 → 立即购买（最高优先级）"
            body="days_to_breach < 30 → buy regardless of price, scenarios, or any other signal. Keeping lines running beats price optimisation. / 安全库存告警天数 < 30 → 不计价格、情景或其他信号，立即购买。保障产线运行优先于价格优化。"
            formula="days_to_breach < 30 → BUY_NOW · Safety Critical" />
          <Step n="2"
            title="P10 too high → BUY_NOW · Price Risk / P10 价格风险过高 → 立即购买"
            body="Even the optimistic 7-day PP price (P10) is more than 5% above today's spot. Waiting is risky even in the best case. / 即使乐观 7 天 PP 价格（P10）也比今日现货高出逾 5%，即便最优情景下等待也有风险。"
            formula="P10_7d > today_spot × 1.05 → BUY_NOW · Price Risk" />
          <Step n="3"
            title="EV favourable + STI high → BUY_TRANCHE / 期望值有利 + STI 偏高 → 分批采购"
            body="E[PP_7d] > today_spot × 1.02 AND STI ≥ 65. Buy a meaningful portion now — not everything. / 7 天期望值 > 今日现货 × 1.02 且 STI ≥ 65，现在采购部分量，非全部。"
            formula="E[PP_7d] > today_spot × 1.02 AND STI ≥ 65 → BUY_TRANCHE" />
          <Step n="4"
            title="Peace signal dominant → HOLD / 和平信号占主导 → 持仓"
            body="Peace probability ≥ 40% — meaningful chance PP corrects ¥800–1,500/MT. Cover safety stock only, hold speculative volume. / 和平概率 ≥ 40%，PP 存在显著回调可能。仅补充安全库存，持有投机性库存。"
            formula="P(peace) ≥ 0.40 → HOLD · Peace Signal Active" />
          <Step n="5"
            title="No urgent signal → HOLD or WAIT / 无紧急信号 → 持仓或等待"
            body="Default fallback when none of the above fires. HOLD may escalate to WAIT via the escalation matrix below. / 以上条件均未触发时的默认回退。HOLD 可通过下方升级矩阵升级为 WAIT。"
            formula="default → HOLD (may escalate to WAIT)" />
        </div>

        <BiH3 en="6.2a WAIT Escalation Matrix (Phase 3-tris)" zh="6.2a WAIT 升级矩阵（Phase 3-tris）" />
        <Bi
          en="HOLD escalates to WAIT when coverage is excessive and the price trend is negative. Four trigger conditions:"
          zh="当覆盖天数过多且价格趋势向下时，HOLD 升级为 WAIT。四个触发条件："
        />
        <BiBullets items={[
          { en: 'Excessive coverage (>90d) + strong_down (E[PP_7d] ≤ spot − 15%) → WAIT', zh: '过度覆盖（>90天）+ 强烈下行（期望值 ≤ 现货 − 15%）→ WAIT' },
          { en: 'Excessive coverage (>90d) + moderate_down (E[PP_7d] ≤ spot − 5%) → WAIT', zh: '过度覆盖（>90天）+ 温和下行（期望值 ≤ 现货 − 5%）→ WAIT' },
          { en: 'Excessive coverage (>90d) + flat (E[PP_7d] within ±5% of spot) → WAIT', zh: '过度覆盖（>90天）+ 持平（期望值在现货 ±5% 以内）→ WAIT' },
          { en: 'Long coverage (60–90d) + strong_down (≤ −15%) → WAIT', zh: '较长覆盖（60–90天）+ 强烈下行（≤ −15%）→ WAIT' },
        ]} />

        <BiH3 en="6.3 Procurement-Native Language (Phase 3-bis)" zh="6.3 采购专业语言呈现（Phase 3-bis）" />
        <Bi
          en="Recommendations display as a 4-line action banner in procurement language. Bilingual labels always shown simultaneously."
          zh="建议以 4 行操作横幅形式呈现，使用采购专业语言。中英文标签始终同时显示。"
        />
        <BiBullets items={[
          { en: 'Line 1: action verb + volume + purpose — e.g. "Buy 200 MT to secure safety stock"', zh: '第 1 行：操作动词 + 数量 + 目的 — 例如"采购 200 吨以补充安全库存"' },
          { en: 'Line 2: today\'s spot price reference', zh: '第 2 行：今日现货价格参考' },
          { en: 'Line 3: 7-day forecast with delta + interpretation (bullish / bearish / neutral)', zh: '第 3 行：7 天预测价格及偏差 + 解读（看涨 / 看跌 / 中性）' },
          { en: 'Line 4: cash required + math breakdown (volume × price)', zh: '第 4 行：所需资金 + 计算明细（数量 × 价格）' },
        ]} />
        <ImpBox>
          <strong style={{ color: 'var(--amber)' }}>Safety always beats price — 安全库存优先于价格</strong>
          <span style={{ fontSize: 12, color: 'var(--t1)', marginLeft: 6 }}>Rule 1 fires before any price signal. The cost of a production line shutdown from PP stockout vastly exceeds the cost of buying at a slightly higher price. 规则 1 先于任何价格信号触发。PP 断库导致产线停工的损失远大于略高价格采购的成本。</span>
        </ImpBox>
      </Section>

      {/* ⑦ VOLUME SIZING */}
      <Section id="s-volume" title={<BT en="⑦ Volume Sizing" zh="⑦ 采购量测算" />} accent="g">
        <Bi
          en="Volume sizing is composed math: action type × price percentile position × inventory overlay. Result is a recommended purchase volume in MT."
          zh="采购量测算为组合计算：操作类型 × 价格百分位位置 × 库存叠加系数，结果为推荐采购量（吨）。"
        />
        <Formula>
          <Fline>monthlyDemandMT <Fo>=</Fo> daily_demand <Fo>×</Fo> <Fv>30</Fv></Fline>
          <Fline>baseVolume      <Fo>=</Fo> monthlyDemandMT <Fo>×</Fo> baseFactor[action]</Fline>
          <Fline>recommended     <Fo>=</Fo> baseVolume <Fo>×</Fo> percentileMultiplier <Fo>×</Fo> inventoryMultiplier</Fline>
          <Fline>&nbsp;</Fline>
          <Fline><Fc>// baseFactor per action  /  各操作类型基础系数</Fc></Fline>
          <Fline>BUY_NOW_SAFETY  <Fo>=</Fo> <Fv>1.5</Fv>   <Fc>// targets ~45 days cover  /  目标约45天覆盖</Fc></Fline>
          <Fline>BUY_NOW_PRICE   <Fo>=</Fo> <Fv>1.0</Fv>   <Fc>// targets ~30 days cover  /  目标约30天覆盖</Fc></Fline>
          <Fline>BUY_TRANCHE     <Fo>=</Fo> <Fv>0.5</Fv>   <Fc>// targets ~15 days cover  /  目标约15天覆盖</Fc></Fline>
          <Fline>HOLD            <Fo>=</Fo> <Fv>0.25</Fv>  <Fc>// safety top-up only       /  仅补充安全库存</Fc></Fline>
          <Fline>WAIT            <Fo>=</Fo> <Fv>0.0</Fv>   <Fc>// no purchase              /  不采购</Fc></Fline>
          <Fline>&nbsp;</Fline>
          <Fline><Fc>// percentileMultiplier: P10 position (price attractive) → 1.2 · P90 → 0.8</Fc></Fline>
          <Fline><Fc>// inventoryMultiplier:  elevated coverage (60–90d) → 0.7 · high (>90d) → 0.5</Fc></Fline>
        </Formula>
        <Bi
          en="The inventory overlay reduces recommended volume when existing coverage is already elevated or high — preventing over-buying even in bullish price scenarios."
          zh="库存叠加系数在现有覆盖天数已偏高或过多时自动降低推荐采购量，避免即使在看涨价格情景下也过度采购。"
        />
      </Section>

      {/* ⑧ MARGINS */}
      <Section id="s-margin" title={<BT en="⑧ Margin Calculations" zh="⑧ 利润计算" />} accent="g">
        <Bi
          en="Margin calculations use Gloss Film 18µm as the anchor product — it has the highest PP-cost sensitivity in the portfolio and its margin turns negative fastest when PP rises."
          zh="利润计算以光膜 18µm 为锚定产品——其 PP 成本占比最高，PP 涨价时利润最先转负。"
        />
        <Formula>
          <Fline><Fc>// PP cost with yield ratio (3% yield loss standard for BOPP extrusion)</Fc></Fline>
          <Fline>PP_cost         <Fo>=</Fo> PP_spot <Fo>×</Fo> yield_ratio              <Fc>// default 1.03</Fc></Fline>
          <Fline>&nbsp;</Fline>
          <Fline><Fc>// Gloss 18µm anchor margin  /  光膜 18µm 锚定利润</Fc></Fline>
          <Fline><Fk>Gloss18_margin</Fk>   <Fo>=</Fo> sell_price_Gloss18 <Fo>−</Fo> PP_cost <Fo>−</Fo> conversion_cost</Fline>
          <Fline>&nbsp;</Fline>
          <Fline><Fc>// Portfolio margin  /  组合利润</Fc></Fline>
          <Fline><Fk>Portfolio_margin</Fk> <Fo>=</Fo> portfolio_avg_sell <Fo>−</Fo> PP_cost <Fo>−</Fo> conversion_cost</Fline>
          <Fline>&nbsp;</Fline>
          <Fline><Fc>// Break-even PP spot for Gloss 18µm  /  光膜 18 盈亏平衡现货价</Fc></Fline>
          <Fline>PP_breakeven    <Fo>=</Fo> (sell_price_Gloss18 <Fo>−</Fo> conversion_cost) <Fo>/</Fo> yield_ratio</Fline>
        </Formula>
        <BiBullets items={[
          { en: 'Conversion cost is ¥1,800/MT as interim default; updated when actual plant cost data is confirmed', zh: '加工转换成本暂定默认 ¥1,800/吨，实际工厂成本数据确认后更新' },
          { en: 'Yield ratio 1.03 = 3% PP yield loss during BOPP film extrusion (industry standard)', zh: '收率系数 1.03 = BOPP 薄膜挤出过程中 PP 原料 3% 损耗（行业标准）' },
          { en: 'Why Gloss 18 is the anchor: lowest sell price + highest PP-cost weight = most sensitive to PP movements', zh: '光膜 18 为何是锚定产品：销售单价最低 + PP 成本占比最高 = 对 PP 价格变动最敏感' },
        ]} />
      </Section>

      {/* ⑨ INCOMPLETE STATE */}
      <Section id="s-incomplete" title={<BT en="⑨ Incomplete State (Package 1)" zh="⑨ 不完整状态（Package 1）" />} accent="a">
        <Bi
          en="Package 1 introduced strict null-handling for KAPS critical fields. When key inputs are missing, the model refuses to run rather than fabricating a recommendation from stale data."
          zh="Package 1 引入了对 KAPS 关键字段的严格空值处理。当关键输入缺失时，模型拒绝运行，而非从过期数据伪造建议。"
        />
        <KeyNote>
          Trigger: kaps_stock_mt == null OR kaps_daily_demand_mt == null → model does not run.{' '}
          触发条件：kaps_stock_mt 为空 或 kaps_daily_demand_mt 为空 → 模型不运行。
        </KeyNote>
        <BiBullets items={[
          { en: 'Effect: ORACLE + Dashboard show an IncompleteState card with a "Go to Data Entry" CTA', zh: '效果：ORACLE 与 Dashboard 显示 IncompleteState 卡片，附"前往数据录入"引导按钮' },
          { en: 'Graceful degrade: stock + demand present but KICP null → model runs, DPI and OTI show amber ★ with tooltip explaining the gap', zh: '优雅降级：库存与需求量已填写但 KICP 为空 → 模型正常运行，DPI 与 OTI 显示琥珀色 ★ 并附提示说明缺口' },
          { en: 'Zero is valid: zero stock = no inventory; zero demand = no production — both are informative states, not missing data', zh: '零值有效：库存为零 = 无库存；需求量为零 = 无生产计划——两者均为有效信息，不属于缺失数据' },
          { en: 'Previous behaviour (pre-Package 1): model fabricated a BUY_NOW recommendation from April snapshot data when KAPS was null — the most dangerous failure mode', zh: '旧版行为（Package 1 之前）：KAPS 为空时，模型以四月快照数据伪造 BUY_NOW 建议——这是最危险的失效模式' },
        ]} />
        <ImpBox>
          <strong style={{ color: 'var(--amber)' }}>Why null ≠ zero — 为何空值不等于零值</strong>
          <span style={{ fontSize: 12, color: 'var(--t1)', marginLeft: 6 }}>Silent defaults are the most dangerous failure mode in procurement models. A null stock value means "we don't know" — treating it as zero triggers a false BUY_NOW. Null must halt the model. 默认静默值是采购模型中最危险的失效模式。库存为空意味着"我们不知道"——将其视为零将触发虚假的 BUY_NOW 建议。空值必须中止模型运行。</span>
        </ImpBox>
      </Section>

      {/* ⑩ VERITAS */}
      <Section id="s-veritas" title={<BT en="⑩ VERITAS Feedback Loop (Phase 4)" zh="⑩ VERITAS 反馈回路（Phase 4）" />} accent="v">
        <Bi
          en="VERITAS closes the loop between predictions and outcomes. Every model run is captured; actual prices are fetched automatically; accuracy metrics are computed and tracked over time."
          zh="VERITAS 打通预测与结果之间的闭环。每次模型运行均被记录，实际价格自动获取，准确率指标持续计算与追踪。"
        />
        <div style={{ marginBottom: 12 }}>
          <Step n="1"
            title="Predictions table — every model run captured / 预测表——记录每次模型运行"
            body="Both manual runs (Save Run button on ORACLE tab) and automated daily cron snapshots are stored with full input state. / 手动运行（ORACLE 页面「保存预测」按钮）和每日定时快照均存储，保留完整输入状态。" />
          <Step n="2"
            title="Actuals cron — daily 08:00 UTC (16:00 China time) / 实际价格定时任务——每日北京时间 16:00（UTC 08:00）"
            body="Fetches DCE PP M1 closing price from Sina Finance. Matches predictions made 7 days prior to compute forecast error. / 从新浪财经获取大连商品交易所 PP M1 收盘价，与 7 天前的预测匹配以计算预测误差。" />
          <Step n="3"
            title="Snapshot cron — daily 00:30 UTC (08:30 China time) / 快照定时任务——每日北京时间 08:30（UTC 00:30）"
            body="Saves a daily model snapshot: current inputs, index scores, scenario probabilities, recommendation. / 保存每日模型快照：当前输入、指数得分、情景概率、采购建议。" />
          <Step n="4"
            title="Weekend skip — both crons skip Saturday + Sunday / 周末跳过——两个定时任务均跳过周六与周日"
            body="DCE does not trade on weekends — fetching actuals on weekend days returns stale or missing data. Snapshot also skips to avoid noise. / 大连商品交易所周末不交易，周末抓取实际价格会返回过期或缺失数据。快照定时任务同样跳过以减少噪音。" />
        </div>
        <BiBullets items={[
          { en: 'MAPE (Mean Absolute Percentage Error) — primary accuracy metric', zh: 'MAPE（平均绝对百分比误差）— 主要准确率指标' },
          { en: 'Hit rate — % of predictions where actual DCE close fell within P10–P90 forecast band', zh: '命中率 — 实际 DCE 收盘价落在 P10–P90 预测区间内的预测占比' },
          { en: 'Bias direction — whether model systematically over- or under-predicts', zh: '偏差方向 — 模型是否系统性地偏高或偏低预测' },
          { en: 'Performance tab (admin-only): shows 7-day / 30-day / all-time aggregates', zh: '性能标签页（仅管理员）：显示 7 天 / 30 天 / 全时段汇总指标' },
          { en: 'Save Run button: manual prediction capture from ORACLE tab at any time', zh: '保存预测按钮：可随时从 ORACLE 页面手动记录预测' },
        ]} />
      </Section>

      {/* ⑪ NEXT */}
      <Section id="s-next" title={<BT en="⑪ What's Next" zh="⑪ 未来规划" />} accent="b">
        <BiBullets items={[
          { en: 'Decision Logic v2 — 25-cell matrix (5 forecast zones × 5 inventory zones), historical price position scoring, demand commitment signal, new BUY_DEMAND_SECURED action', zh: '决策逻辑 v2 — 25 格决策矩阵（5 个预测区间 × 5 个库存区间）、历史价格位置评分、需求锁定信号、新操作 BUY_DEMAND_SECURED' },
          { en: 'LLM signal layer (Phase 5) — daily news classifier using Claude Haiku: scans supply chain news, flags Hormuz / PP plant events, adjusts scenario priors automatically', zh: 'LLM 信号层（Phase 5）— 使用 Claude Haiku 的每日新闻分类器：扫描供应链新闻，标记霍尔木兹 / PP 工厂相关事件，自动调整情景先验概率' },
          { en: 'Bonded channel — model currently covers non-bonded inventory only (~65–70% of demand); bonded warehouse logic to be defined', zh: '保税渠道 — 当前模型仅覆盖非保税库存（约 65–70% 的需求）；保税仓库逻辑待定' },
          { en: 'Multi-horizon forecasts — concurrent 7-day, 30-day, 60-day forecasts', zh: '多时段预测 — 7 天、30 天、60 天并行预测' },
          { en: 'Role-tailored views — Executive summary, Procurement detail, Sales briefing, IT/Ops, Marketing', zh: '角色定制视图 — 高管摘要、采购详情、销售简报、IT/运营、市场部' },
          { en: 'KAPS / KICP API integration — currently manual entry; in IT backlog', zh: 'KAPS / KICP API 集成 — 当前为手动录入；已列入 IT 开发队列' },
        ]} />
      </Section>

      {/* ⑫ GLOSSARY */}
      <Section id="s-glossary" title={<BT en="⑫ Glossary" zh="⑫ 词汇表" />} accent="b">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, margin: '10px 0' }}>
          <thead>
            <tr>
              {['Term', 'Full name (EN)', '中文全称', 'Notes'].map(h => (
                <th key={h} style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.08em', padding: '7px 12px', borderBottom: '1px solid var(--border)', textAlign: 'left', background: 'var(--bg3)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ['BOPP',  'Biaxially Oriented Polypropylene Film', '双向拉伸聚丙烯薄膜',   'End product — the film Kunlene manufactures'],
              ['PP',    'Polypropylene',                         '聚丙烯',              'Primary raw material feedstock'],
              ['WAC',   'Weighted Average Cost',                 '加权平均成本',         'KAPS inventory cost basis'],
              ['CFR',   'Cost + Freight',                        '成本加运费',           'Import price basis (excludes import duty)'],
              ['MAPE',  'Mean Absolute Percentage Error',        '平均绝对百分比误差',    'Primary VERITAS accuracy metric'],
              ['P10',   '10th percentile forecast',              '第 10 百分位预测',     'Probability-weighted optimistic price bound'],
              ['P50',   '50th percentile (median) forecast',     '第 50 百分位预测',     'Central / expected-value forecast'],
              ['P90',   '90th percentile forecast',              '第 90 百分位预测',     'Probability-weighted pessimistic price bound'],
              ['STI',   'Supply Tightness Index',                '供应紧张指数',         '0–100; physical PP supply tightness'],
              ['MSI',   'Market Supply Index',                   '市场供应指数',         '0–100; industry-level supply pressure'],
              ['DPI',   'BOPP Demand Pressure Index',            'BOPP 需求压力指数',    '0–100; downstream demand strength'],
              ['OTI',   'Order Trend Index',                     '订单趋势指数',         '−100 to +100; order book momentum'],
              ['DCE',   'Dalian Commodity Exchange',             '大连商品交易所',        'PP M1 futures reference — China primary exchange'],
              ['KAPS',  'Kunlene internal MRP system',           '昆仑恩内部 MRP 系统',  'Stock, transit, WAC, safety stock, daily demand'],
              ['KICP',  'Kunlene internal film order system',    '昆仑恩内部薄膜订单系统','Sell prices, portfolio qty, order trend'],
              ['MT',    'Metric Tonne',                          '公吨',                'Standard unit for all volume figures'],
            ].map((r, i) => (
              <tr key={i}>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--blue)' }}>{r[0]}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--t0)', fontWeight: 500 }}>{r[1]}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--t1)' }}>{r[2]}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--t2)', fontSize: 11 }}>{r[3]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}
